import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, RefreshCw, Truck, XCircle } from "lucide-react";
import { trackOrder } from "../services/orderService";
import { formatISTDateTime } from "../utils/dateTime";

const timelineSteps = [
  { key: "pending", label: "Order Placed" },
  { key: "confirmed", label: "Confirmed" },
  { key: "processing", label: "Processing" },
  { key: "shipped", label: "Shipped" },
  { key: "out_for_delivery", label: "Out for Delivery" },
  { key: "delivered", label: "Delivered" },
];

const statusLabel = (status = "") => status.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [contact, setContact] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Track Order | Cynos";
  }, []);

  const fetchOrder = useCallback(async (silent = false) => {
    if (!orderId.trim() || !contact.trim()) {
      if (!silent) setError("Please enter both order ID and email or phone.");
      return;
    }
    if (!silent) {
      setError("");
      setResult(null);
      setLoading(true);
    }
    try {
      const data = await trackOrder(orderId, contact.trim());
      setResult(data);
    } catch (err) {
      if (!silent) {
        const detail = err?.response?.data?.detail || "Order not found. Check the ID and contact details.";
        setError(detail);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [contact, orderId]);

  useEffect(() => {
    if (!result) return undefined;
    const id = setInterval(() => fetchOrder(true), 30000);
    return () => clearInterval(id);
  }, [fetchOrder, result]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrder(false);
  };

  const isCancelled = result?.status === "cancelled";
  const activeIndex = result && !isCancelled ? timelineSteps.findIndex((step) => step.key === result.status) : -1;
  const currentIndex = activeIndex >= 0 ? activeIndex : result?.status === "payment_pending" ? 0 : -1;

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-6xl space-y-12">
        <section className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Track Order</p>
          <h1 className="mt-4 text-5xl font-anton uppercase text-white">Check your shipment status.</h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-400">Enter your order ID and contact details. This page reads the latest order status from the database.</p>
        </section>

        <div className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Order ID</label>
                <input value={orderId} onChange={(e) => setOrderId(e.target.value)} className="w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none transition focus:border-cynos-red" placeholder="Full order ID or first 8 characters" />
              </div>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Email or Phone</label>
                <input value={contact} onChange={(e) => setContact(e.target.value)} className="w-full rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none transition focus:border-cynos-red" placeholder="customer@example.com or 9876543210" />
              </div>
              {error && <div className="rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}
              <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:opacity-70">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                {loading ? "Tracking..." : "Track Order"}
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="rounded-3xl border border-white/10 bg-cynos-card p-8">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Order Timeline</p>
              {result && (
                <button onClick={() => fetchOrder(true)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              )}
            </div>

            {isCancelled && (
              <div className="mt-6 rounded-3xl border border-cynos-red/30 bg-cynos-red/10 p-5">
                <div className="flex items-center gap-3 text-cynos-red">
                  <XCircle className="h-5 w-5" />
                  <p className="font-semibold uppercase">Order Cancelled</p>
                </div>
                <p className="mt-3 text-sm text-zinc-300">{result.cancellation_reason || "This order has been cancelled."}</p>
                {result.cancelled_at && <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">{formatISTDateTime(result.cancelled_at)}</p>}
              </div>
            )}

            <div className="mt-6 space-y-4">
              {timelineSteps.map((step, index) => (
                <div key={step.key} className="flex items-start gap-4">
                  <div className={`mt-1 h-3 w-3 rounded-full ${!isCancelled && index <= currentIndex ? "bg-cynos-red" : "bg-white/10"}`} />
                  <div>
                    <p className="text-sm font-semibold text-white">{step.label}</p>
                    <p className="text-sm text-zinc-500">{!isCancelled && index === currentIndex ? "Current step" : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Tracking Result</p>
            <div className="mt-6 grid gap-4 md:grid-cols-4">
              {[
                { label: "Order ID", value: result.id.slice(0, 8).toUpperCase() },
                { label: "Status", value: statusLabel(result.status) },
                { label: "Payment", value: statusLabel(result.payment_status) },
                { label: "Updated", value: formatISTDateTime(result.updated_at || result.created_at) },
              ].map((item) => (
                <div key={item.label} className="rounded-3xl border border-white/10 bg-black/60 p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{item.label}</p>
                  <p className="mt-3 text-lg font-semibold text-white">{item.value}</p>
                </div>
              ))}
            </div>

            {(result.awb_number || result.courier_name || result.tracking_status) && !isCancelled && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-3xl border border-cynos-red/30 bg-cynos-red/5 p-8">
                <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">NimbusPost Shipment</p>
                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Courier Partner</p>
                    <p className="mt-3 text-lg font-semibold text-white">{result.courier_name || result.shipping_partner || "NimbusPost"}</p>
                  </div>
                  {result.awb_number && (
                    <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">AWB Number</p>
                      <p className="mt-3 text-lg font-semibold text-white">{result.awb_number}</p>
                    </div>
                  )}
                  <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Shipping Status</p>
                    <p className="mt-3 text-lg font-semibold text-white">{statusLabel(result.tracking_status || result.shipping_status || "Created")}</p>
                  </div>
                  {result.tracking_url && (
                    <a href={result.tracking_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-2xl bg-cynos-red px-5 py-4 text-sm font-semibold uppercase text-black transition hover:bg-cynos-red/90">
                      Track Shipment
                    </a>
                  )}
                </div>
              </motion.div>
            )}

            {result.status_history?.length > 0 && (
              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">Status History</p>
                <div className="mt-4 space-y-3">
                  {result.status_history.map((entry, index) => (
                    <div key={`${entry.status}-${index}`} className="flex flex-col gap-1 rounded-2xl border border-white/10 bg-black/50 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-sm font-semibold uppercase text-white">{statusLabel(entry.status)}</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{formatISTDateTime(entry.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Qikink Tracking Info */}
            {(result.qikink_awb || result.qikink_courier) && !isCancelled && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-3xl border border-cynos-red/30 bg-cynos-red/5 p-8">
                <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Shipment Details</p>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                  {result.qikink_courier && (
                    <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Courier Partner</p>
                      <p className="mt-3 text-lg font-semibold text-white">{result.qikink_courier}</p>
                    </div>
                  )}
                  {result.qikink_awb && (
                    <div className="rounded-2xl border border-white/10 bg-black/60 p-5">
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">AWB Number</p>
                      <p className="mt-3 text-lg font-semibold text-white">{result.qikink_awb}</p>
                    </div>
                  )}
                  {result.qikink_tracking_url && (
                    <a href={result.qikink_tracking_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-2xl bg-cynos-red px-5 py-4 text-sm font-semibold uppercase text-black transition hover:bg-cynos-red/90">
                      Track Shipment
                    </a>
                  )}
                </div>
                {result.fulfillment_status && (
                  <p className="mt-4 text-xs uppercase tracking-[0.2em] text-zinc-500">
                    Fulfillment: {statusLabel(result.fulfillment_status)}
                  </p>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </motion.div>
    </main>
  );
}
